// PATH: src/components/documents/ObjectiveKnowledgePanel.tsx
import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useKnowledgeApi } from "../../api/knowledge/knowledge";
import type {
  FactOverride,
  FactReview,
  KnowledgeResponse,
  KnowledgeScope,
} from "../../api/knowledge/knowledge";
import { useObjectivesApi } from "../../api/objectives/objectives";
import type { DocumentObjective, ObjectiveContextResponse } from "../../api/objectives/objectives";
import { computeTraits, possibleForms } from "./knowledgeForms";
import { StatusPill, errorMessage, useConfirm, useToast } from "../ui";
import { useAuth } from "../../context/AuthContext";

/**
 * Base de faits d'un objectif (étape 4 §4), affichée DANS le panneau du
 * chapitre (vue de détail de l'onglet « Objectifs et faits »).
 *
 * Ce que l'équipe qui importe doit pouvoir décider ici : la base est-elle
 * juste, et que permet-elle ? D'où la synthèse (traits mesurés + formes
 * rendues possibles) et, par fait, le passage source, le verdict de relecture,
 * les signalements et la bascule actif/désactivé. Une base fausse est corrigée
 * une fois pour tous.
 */

interface Props {
  objective: DocumentObjective;
  /** Retour à la liste des objectifs. */
  onBack: () => void;
  /** Ré-extraction faite : la liste des objectifs doit être rechargée. */
  onExtracted?: () => void;
}

type OverrideMap = Record<string, FactOverride>;

const shortHash = (hash: string) => (hash ? hash.slice(0, 8) : "—");

/** Les overrides arrivent dans `disabled` : y figurer vaut « désactivé ». */
const toOverrideMap = (overrides: FactOverride[] | undefined): OverrideMap => {
  const map: OverrideMap = {};
  (overrides ?? []).forEach((override) => {
    map[override.fact_id] = { ...override, disabled: override.disabled ?? true };
  });
  return map;
};

const disabledByLabel = (disabledBy: string | undefined): string => {
  if (!disabledBy) return "désactivé";
  if (disabledBy === "review") return "désactivé par la relecture";
  if (disabledBy === "report") return "désactivé par signalement";
  if (disabledBy.startsWith("editor")) return "désactivé par un éditeur";
  return `désactivé (${disabledBy})`;
};

const relationTypeLabel: Record<string, string> = {
  functional: "fonctionnelle",
  asymmetric: "asymétrique",
  symmetric: "symétrique",
  undeclared: "sens non déclaré",
};

const relationSenseLabel: Record<string, string> = {
  functional: "une seule cible pour ce label",
  asymmetric: "sens unique, l'inverse est faux",
  symmetric: "vraie dans les deux sens",
  undeclared: "le modèle ne s'est engagé sur aucun sens : aucun leurre",
};

const ReviewVerdict: React.FC<{ review?: FactReview }> = ({ review }) => {
  if (!review) return null;
  return (
    <>
      <StatusPill tone={review.supported ? "success" : "error"}>
        {review.supported ? "passage confirmé" : "non confirmé par le passage"}
      </StatusPill>
      <StatusPill tone={review.type_ok ? "success" : "warning"}>
        {review.type_ok ? "type vérifié" : "type douteux"}
      </StatusPill>
      {review.note ? (
        <Typography variant="caption" color="text.secondary" dir="auto">
          {review.note}
        </Typography>
      ) : null}
    </>
  );
};

interface FactRowProps {
  factId: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  chunk: number;
  /** 8 hex de l'objectif porteur, seulement quand ce n'est pas celui affiché. */
  foreignOwner?: string | null;
  /** Pastilles propres au type de fait (sens non déclaré, non relu…). */
  extraChips?: React.ReactNode;
  active: boolean;
  pending: boolean;
  override?: FactOverride;
  review?: FactReview;
  reportCount: number;
  passage: React.ReactNode;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleActive: (active: boolean) => void;
  onSaveNote: (note: string) => void;
  /** Relecteur : ni bascule ni note, la note existante reste lisible. */
  readOnly?: boolean;
}

const FactRow: React.FC<FactRowProps> = ({
  factId,
  primary,
  secondary,
  chunk,
  foreignOwner,
  extraChips,
  active,
  pending,
  override,
  review,
  reportCount,
  passage,
  expanded,
  onToggleExpand,
  onToggleActive,
  onSaveNote,
  readOnly = false,
}) => {
  const [note, setNote] = React.useState(override?.note ?? "");

  // La note peut revenir du serveur après un PATCH : on resynchronise.
  React.useEffect(() => {
    setNote(override?.note ?? "");
  }, [override?.note]);

  return (
    <Box sx={{ px: 2, py: 1.5, bgcolor: active ? "transparent" : "action.hover" }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Tooltip title={readOnly ? (active ? "Fait actif" : "Fait désactivé") : active ? "Désactiver ce fait" : "Réactiver ce fait"}>
          <Switch
            checked={active}
            disabled={pending || readOnly}
            onChange={(event) => onToggleActive(event.target.checked)}
            inputProps={{ "aria-label": `fait ${factId}` }}
            sx={{ mt: 0.25 }}
          />
        </Tooltip>

        <Box flex={1} minWidth={0} sx={{ opacity: active ? 1 : 0.7 }}>
          <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
            <Typography variant="body1" component="span" dir="auto">
              {primary}
            </Typography>
            <Typography variant="caption" className="mono" color="text.secondary">
              {factId}
            </Typography>
            {pending ? <CircularProgress size={12} /> : null}
          </Stack>

          {secondary ? (
            <Typography variant="body2" color="text.secondary" dir="auto" sx={{ mt: 0.25 }}>
              {secondary}
            </Typography>
          ) : null}

          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
            {!active ? <StatusPill tone="neutral">{disabledByLabel(override?.disabled_by)}</StatusPill> : null}
            {reportCount > 0 ? <StatusPill tone="warning">{`signalé ${reportCount} fois`}</StatusPill> : null}
            {foreignOwner ? (
              <Tooltip
                title={
                  "Ce fait est porté par un AUTRE objectif du voisinage " +
                  `(${foreignOwner}…). Le désactiver ici l'écrit chez lui : ` +
                  "il disparaîtra aussi des exercices de cet autre objectif."
                }
              >
                <span>
                  <StatusPill tone="info">{`venu de ${foreignOwner}`}</StatusPill>
                </span>
              </Tooltip>
            ) : null}
            {extraChips}
            <ReviewVerdict review={review} />
            <Button
              size="small"
              color="inherit"
              onClick={onToggleExpand}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ minHeight: 24, py: 0, color: "text.secondary" }}
            >
              Passage {chunk + 1}
            </Button>
          </Stack>

          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: "background.default" }}>{passage}</Box>
          </Collapse>

          {(!readOnly || note) && (
            <TextField
              value={note}
              onChange={(event) => setNote(event.target.value)}
              onBlur={() => {
                if ((override?.note ?? "") !== note) onSaveNote(note);
              }}
              placeholder="Note éditoriale : pourquoi ce fait est faux, ce qu'il faudrait"
              fullWidth
              variant="standard"
              disabled={pending}
              sx={{ mt: 1 }}
              InputProps={{ readOnly, sx: { fontSize: "0.8125rem" } }}
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
};

const TraitStat: React.FC<{ label: string; value: number; tone?: "warning" | "info" }> = ({
  label,
  value,
  tone,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="h5"
      component="div"
      sx={{ color: tone === "warning" && value > 0 ? "warning.dark" : tone === "info" && value > 0 ? "info.dark" : "text.primary" }}
    >
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

export const ObjectiveKnowledgePanel: React.FC<Props> = ({ objective, onBack, onExtracted }) => {
  // Relecteur : base de faits en lecture seule (ni bascule, ni note, ni ré-extraction).
  const { canEdit } = useAuth();
  const { getKnowledge, patchFact, extractDocument } = useKnowledgeApi();
  const { getObjectiveContext } = useObjectivesApi();
  const confirm = useConfirm();
  const toast = useToast();

  const objectiveId = objective.id;

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [factError, setFactError] = React.useState<string | null>(null);
  const [data, setData] = React.useState<KnowledgeResponse | null>(null);
  const [overrides, setOverrides] = React.useState<OverrideMap>({});
  const [pending, setPending] = React.useState<string[]>([]);
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const [tab, setTab] = React.useState(0);
  /* `own` par défaut : l'écran éditorial doit montrer ce que CET objectif
     porte. Le voisinage (ce que tc_learn sert vraiment) est une bascule. */
  const [scope, setScope] = React.useState<KnowledgeScope>("own");
  const [reloadKey, setReloadKey] = React.useState(0);

  const [context, setContext] = React.useState<ObjectiveContextResponse | null>(null);
  const [contextLoading, setContextLoading] = React.useState(false);
  const [contextError, setContextError] = React.useState<string | null>(null);

  const [extracting, setExtracting] = React.useState(false);
  const [extractResult, setExtractResult] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setFactError(null);
    setData(null);
    setOverrides({});
    setExpanded({});
    setContext(null);
    setContextError(null);

    getKnowledge(objectiveId, scope)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setOverrides(toOverrideMap(res?.disabled));
      })
      .catch(() => !cancelled && setError("Impossible de charger la base de faits."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [objectiveId, scope, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setTab(0);
    setExtractResult(null);
  }, [objectiveId]);

  const unit = data?.unit;

  const disabledIds = React.useMemo(() => {
    const ids = new Set<string>();
    Object.values(overrides).forEach((override) => {
      // Un override périmé ne désigne plus un fait servi : le serveur ne
      // l'applique pas, les traits ne doivent pas faire comme s'il l'était.
      if (override.disabled && !override.stale) ids.add(override.fact_id);
    });
    return ids;
  }, [overrides]);

  /* Traits recalculés sur les faits actifs : les formes possibles suivent la
     bascule immédiatement, sans recharger l'unité. */
  const traits = React.useMemo(() => (unit ? computeTraits(unit, disabledIds) : null), [unit, disabledIds]);
  const forms = React.useMemo(() => (traits ? possibleForms(traits) : []), [traits]);

  const termById = React.useMemo(() => {
    const map: Record<string, string> = {};
    (unit?.terms ?? []).forEach((term) => {
      map[term.id] = term.term;
    });
    return map;
  }, [unit]);

  const termLabel = (id: string | undefined) => (id ? termById[id] ?? id : "—");

  /* Un override « à revoir » ne désigne plus aucun fait servi (renuméroté ou
     disparu à la ré-extraction), donc il n'est PAS appliqué. Pas d'endpoint
     pour le supprimer, mais le taire reviendrait à croire une correction
     encore en place. */
  const staleOverrides = React.useMemo(
    () => Object.values(overrides).filter((override) => override.stale),
    [overrides],
  );

  /** 8 hex de l'objectif porteur, ou `null` si le fait est propre. */
  const foreignOwner = (owner: string | undefined): string | null => {
    if (!owner || owner === objectiveId) return null;
    return owner.slice(0, 8);
  };

  const isActive = (factId: string) => !disabledIds.has(factId);
  const isPending = (factId: string) => pending.includes(factId);
  const reportCount = (factId: string) => data?.reports?.[factId] ?? 0;

  const savePatch = async (factId: string, disabled: boolean, note: string | undefined) => {
    const previous = overrides[factId];
    /* Toujours envoyer la note, même vide : un PATCH sans champ `note`
       laisserait le serveur libre de garder l'ancienne. */
    const payloadNote = note ?? previous?.note ?? "";

    // Optimiste : la ligne réagit tout de suite, retour arrière si refus.
    setOverrides((prev) => ({
      ...prev,
      [factId]: {
        fact_id: factId,
        disabled,
        disabled_by: previous?.disabled_by ?? "editor",
        note: payloadNote,
      },
    }));
    setPending((prev) => [...prev, factId]);
    setFactError(null);

    try {
      const res = await patchFact(objectiveId, factId, { disabled, note: payloadNote });
      setOverrides((prev) => ({
        ...prev,
        [factId]: { ...res.data, disabled: res.data.disabled ?? disabled },
      }));
    } catch (e) {
      setOverrides((prev) => {
        const next = { ...prev };
        if (previous) next[factId] = previous;
        else delete next[factId];
        return next;
      });
      setFactError(`Le fait ${factId} n'a pas pu être enregistré : ${errorMessage(e)}`);
    } finally {
      setPending((prev) => prev.filter((id) => id !== factId));
    }
  };

  const handleToggleActive = (factId: string, active: boolean) => {
    void savePatch(factId, !active, overrides[factId]?.note ?? undefined);
  };

  const handleSaveNote = (factId: string, note: string) => {
    void savePatch(factId, disabledIds.has(factId), note);
  };

  /* Le passage source vient du contexte de l'objectif (mêmes passages que
     l'extraction) : un seul appel, chargé au premier dépliage. */
  const loadContext = () => {
    if (context || contextLoading) return;
    setContextLoading(true);
    setContextError(null);
    getObjectiveContext(objectiveId)
      .then((res) => setContext(res.data))
      .catch(() => setContextError("Impossible de charger les passages du cours."))
      .finally(() => setContextLoading(false));
  };

  const handleToggleExpand = (factId: string) => {
    setExpanded((prev) => {
      const next = !prev[factId];
      if (next) loadContext();
      return { ...prev, [factId]: next };
    });
  };

  const renderPassage = (chunk: number): React.ReactNode => {
    if (contextLoading) return <Skeleton variant="text" width="80%" />;
    if (contextError) {
      return (
        <Typography variant="body2" color="error">
          {contextError}
        </Typography>
      );
    }
    const found = (context?.chunks ?? []).find((item) => item.ordinal === chunk);
    if (!found) {
      return (
        <Typography variant="body2" color="text.secondary">
          Passage n° {chunk + 1} absent du contexte de l'objectif : la provenance est probablement périmée
          (chapitre réimporté sans ré-extraction).
        </Typography>
      );
    }
    return (
      <Typography variant="body2" color="text.secondary" dir="auto" sx={{ whiteSpace: "pre-wrap" }}>
        {found.text}
      </Typography>
    );
  };

  const handleExtract = async () => {
    const ok = await confirm({
      title: "Ré-extraire les bases de faits du chapitre ?",
      message:
        "Cette opération remplace l'unité de faits de TOUS les objectifs du chapitre, et avec elle les " +
        "désactivations portées par la relecture.\n\nLes notes éditoriales et les désactivations manuelles " +
        "restent attachées aux identifiants de faits : un fait renommé par la nouvelle extraction perd sa correction.",
      confirmLabel: "Ré-extraire",
    });
    if (!ok) return;
    setExtracting(true);
    setFactError(null);
    setExtractResult(null);
    try {
      const res = await extractDocument(objective.document_id);
      /* Les compteurs qui font diagnostic : `skipped_unchanged` dit qu'un run
         n'a rien remplacé, `failed_windows` qu'un passage n'a rendu aucun
         fait — deux cas invisibles autrement. */
      const parts = [
        `${res.data.units} unité(s) ré-extraite(s) sur ${res.data.objectives} objectif(s)`,
        `${res.data.disabled_by_review} fait(s) désactivé(s) par la relecture`,
      ];
      if (res.data.demoted_by_review != null) parts.push(`${res.data.demoted_by_review} type(s) rétrogradé(s)`);
      if (res.data.skipped_unchanged) parts.push(`${res.data.skipped_unchanged} objectif(s) inchangé(s), sauté(s)`);
      if (res.data.failed_windows) parts.push(`${res.data.failed_windows} passage(s) en échec`);
      setExtractResult(parts.join(" · ") + ".");
      toast.success("Bases de faits ré-extraites.");
      onExtracted?.();
      // L'unité de l'objectif courant vient d'être remplacée.
      setReloadKey((k) => k + 1);
    } catch (e) {
      setFactError(`La ré-extraction a échoué : ${errorMessage(e)}`);
    } finally {
      setExtracting(false);
    }
  };

  const counts = {
    terms: unit?.terms?.length ?? 0,
    relations: unit?.relations?.length ?? 0,
    steps: unit?.steps?.length ?? 0,
    dates: unit?.dates?.length ?? 0,
    misconceptions: unit?.misconceptions?.length ?? 0,
    categories: unit?.categories?.length ?? 0,
  };

  /**
   * Ce que la ligne d'une relation dit en plus de son type effectif : un sens
   * non déclaré n'autorise aucun leurre, et une relation que la relecture n'a
   * pas jugée n'est pas acquittée pour autant.
   */
  const relationChips = (relation: { type: string; reviewed?: boolean }): React.ReactNode => (
    <>
      {relation.type === "undeclared" ? (
        <Tooltip title="Type absent à l'extraction : ni inversion ni substitution ne peuvent s'appuyer dessus.">
          <span>
            <StatusPill tone="warning">sens non déclaré</StatusPill>
          </span>
        </Tooltip>
      ) : null}
      {relation.reviewed === false ? (
        <Tooltip title="La relecture n'a rendu aucun verdict sur ce fait : son silence ne vaut pas acquittement.">
          <span>
            <StatusPill tone="neutral">non relu</StatusPill>
          </span>
        </Tooltip>
      ) : null}
    </>
  );

  const factRow = (params: {
    factId: string;
    primary: React.ReactNode;
    secondary?: React.ReactNode;
    chunk: number;
    owner?: string;
    extraChips?: React.ReactNode;
  }) => (
    <FactRow
      key={params.factId}
      factId={params.factId}
      primary={params.primary}
      secondary={params.secondary}
      chunk={params.chunk}
      foreignOwner={foreignOwner(params.owner)}
      extraChips={params.extraChips}
      active={isActive(params.factId)}
      pending={isPending(params.factId)}
      override={overrides[params.factId]}
      review={data?.review?.[params.factId]}
      reportCount={reportCount(params.factId)}
      passage={renderPassage(params.chunk)}
      expanded={Boolean(expanded[params.factId])}
      onToggleExpand={() => handleToggleExpand(params.factId)}
      onToggleActive={(active) => handleToggleActive(params.factId, active)}
      onSaveNote={(note) => handleSaveNote(params.factId, note)}
      readOnly={!canEdit}
    />
  );

  const emptyList = (label: string) => (
    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
      {label}
    </Typography>
  );

  const renderFacts = (): React.ReactNode[] | React.ReactNode => {
    if (!unit) return null;
    switch (tab) {
      case 0:
        return counts.terms
          ? unit.terms.map((term) =>
              factRow({
                factId: term.id,
                primary: <strong>{term.term}</strong>,
                secondary: term.definition,
                chunk: term.chunk,
                owner: term.owner,
              }),
            )
          : emptyList("Aucun terme.");
      case 1:
        return counts.relations
          ? unit.relations.map((relation) =>
              factRow({
                factId: relation.id,
                primary: (
                  <>
                    {termLabel(relation.from)} <em>→ {relation.label} →</em> {termLabel(relation.to)}
                  </>
                ),
                secondary:
                  `${relationTypeLabel[relation.type] ?? relation.type} · ` +
                  `${relationSenseLabel[relation.type] ?? "sens non typé"}` +
                  (relation.declared_type && relation.declared_type !== relation.type
                    ? ` · déclarée ${
                        relationTypeLabel[relation.declared_type] ?? relation.declared_type
                      }, rétrogradée par la relecture`
                    : ""),
                chunk: relation.chunk,
                owner: relation.owner,
                extraChips: relationChips(relation),
              }),
            )
          : emptyList("Aucune relation.");
      case 2:
        return counts.steps
          ? [...unit.steps]
              .sort((a, b) => a.order - b.order)
              .map((step) =>
                factRow({
                  factId: step.id,
                  primary: `${step.order}. ${step.text}`,
                  chunk: step.chunk,
                  owner: step.owner,
                }),
              )
          : emptyList("Aucune étape.");
      case 3:
        return counts.dates
          ? unit.dates.map((date) =>
              factRow({
                factId: date.id,
                primary: <strong>{date.when}</strong>,
                secondary: date.event,
                chunk: date.chunk,
                owner: date.owner,
              }),
            )
          : emptyList("Aucune date.");
      case 4:
        return counts.misconceptions
          ? unit.misconceptions.map((misconception) =>
              factRow({
                factId: misconception.id,
                primary: misconception.claim,
                secondary:
                  `Correction : ${misconception.correction} · type ${misconception.error_type}` +
                  (misconception.label
                    ? ` · ${termLabel(misconception.from)} → ${misconception.label} → ${termLabel(misconception.to)}`
                    : ""),
                chunk: misconception.chunk,
                owner: misconception.owner,
              }),
            )
          : emptyList("Aucune idée fausse.");
      default:
        return counts.categories
          ? unit.categories.map((category) =>
              factRow({
                factId: category.id,
                primary: <strong>{category.name}</strong>,
                secondary: (category.members ?? []).map(termLabel).join(", ") || "aucun membre",
                chunk: category.chunk,
                owner: category.owner,
              }),
            )
          : emptyList("Aucune catégorie.");
    }
  };

  /* L'unité d'un objectif est atomique (2 à 4 termes) : c'est le voisinage
     qui atteint les seuils et c'est lui que tc_learn reçoit, mais on corrige
     d'abord ce que CET objectif porte. La bascule reste accessible même
     quand l'objectif n'a rien à lui. */
  const scopeToggle = (
    <FormControlLabel
      sx={{ m: 0 }}
      control={
        <Switch
          checked={scope === "neighborhood"}
          onChange={(event) => setScope(event.target.checked ? "neighborhood" : "own")}
          sx={{ mr: 1 }}
        />
      }
      label={<Typography variant="body2">Inclure les passages voisins (unité servie à l'élève)</Typography>}
    />
  );

  const factsList = renderFacts();

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Button size="small" color="inherit" startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ ml: -1, mb: 0.5 }}>
            Tous les objectifs
          </Button>
          <Typography variant="subtitle1" dir="auto">
            {objective.ordinal ? `${objective.ordinal}. ` : ""}
            {objective.text}
          </Typography>
        </Box>
        {canEdit && (
          <Button
            variant="outlined"
            size="small"
            startIcon={extracting ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={() => void handleExtract()}
            disabled={extracting}
            sx={{ flexShrink: 0 }}
          >
            {extracting ? "Ré-extraction…" : "Ré-extraire les faits"}
          </Button>
        )}
      </Stack>

      {factError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFactError(null)}>
          {factError}
        </Alert>
      ) : null}
      {extractResult ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setExtractResult(null)}>
          {extractResult}
        </Alert>
      ) : null}

      {loading ? (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={48} />
          <Skeleton variant="rounded" height={48} />
        </Stack>
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>
              Réessayer
            </Button>
          }
        >
          {error}
        </Alert>
      ) : !data ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {scope === "own"
              ? "Cet objectif ne porte aucun fait qui lui soit propre. Il peut quand même recevoir une unité par le voisinage."
              : "Aucune unité de faits, même avec les passages voisins : les formes construites (carte de concepts, " +
                "analogie) ne seront pas servies, l'élève reçoit un QCM. Une ré-extraction peut la créer."}
          </Typography>
          {scopeToggle}
        </Paper>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ sm: "center" }}
              sx={{ mb: 1.5 }}
            >
              {scopeToggle}
              <Typography variant="caption" color="text.secondary" className="mono">
                {[
                  `empreinte ${shortHash(data.source_hash)}`,
                  data.version,
                  data.engine ? `moteur ${data.engine}` : null,
                  `langue ${data.language || "?"}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Stack>

            <Typography variant="overline" color="text.secondary">
              Traits mesurés (faits actifs)
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(5, 1fr)" },
                gap: 1.5,
                mt: 0.5,
                mb: 2,
              }}
            >
              <TraitStat label="termes" value={traits?.terms ?? 0} />
              <Tooltip title="Ce qui décide de l'appariement : un terme sans définition ne fait pas de paire.">
                <Box>
                  <TraitStat label="termes définis" value={traits?.defined_terms ?? 0} tone="info" />
                </Box>
              </Tooltip>
              <TraitStat label="fonctionnelles" value={traits?.relations?.functional ?? 0} />
              <Tooltip title="Ce qui décide vraiment de l'analogie : l'unicité constatée dans l'unité servie, sur un libellé déclaré fonctionnel.">
                <Box>
                  <TraitStat label="fonct. calculées" value={traits?.relations?.functional_computed ?? 0} tone="info" />
                </Box>
              </Tooltip>
              <TraitStat label="asymétriques" value={traits?.relations?.asymmetric ?? 0} />
              <TraitStat label="symétriques" value={traits?.relations?.symmetric ?? 0} />
              <TraitStat label="sans sens déclaré" value={traits?.relations?.undeclared ?? 0} tone="warning" />
              <TraitStat label="étapes" value={traits?.steps ?? 0} />
              <TraitStat label="dates" value={traits?.dates ?? 0} />
              {/* Aucune forme ne naît des idées fausses : tc_learn s'en sert comme
                  leurres (analogie, QCM). L'infobulle le dit pour qu'un compteur
                  élevé ne soit pas lu comme un exercice à venir. */}
              <Tooltip title="N'ouvrent aucune forme à elles seules. Formulées comme une relation sur un libellé fonctionnel, elles servent de leurre à l'analogie (et la complètent à 3 triplets) ; toutes sont données au générateur de QCM comme distracteurs, jamais comme vérité.">
                <Box>
                  <TraitStat label="idées fausses" value={traits?.misconceptions ?? 0} />
                </Box>
              </Tooltip>
              <TraitStat label="catégories" value={traits?.categories ?? 0} />
              <Tooltip title="Ce qui décide du tri : les catégories d'au moins 2 termes actifs, ou à défaut les arrivées partagées par 2 termes sous un même libellé de relation.">
                <Box>
                  <TraitStat
                    label="groupes de tri"
                    value={Math.max(traits?.declared_groups ?? 0, traits?.computed_groups ?? 0)}
                    tone="info"
                  />
                </Box>
              </Tooltip>
            </Box>
            {Object.keys(traits?.functional_labels ?? {}).length > 0 && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {Object.entries(traits?.functional_labels ?? {}).map(([label, n]) => (
                  <Chip key={label} size="small" variant="outlined" label={`« ${label} » ×${n}`} />
                ))}
              </Stack>
            )}

            <Typography variant="overline" color="text.secondary">
              Formes rendues possibles
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {forms.map((form) => (
                <Tooltip
                  key={form.key}
                  title={
                    !form.served
                      ? `Non servie par tc_learn : ${form.requirement}`
                      : form.possible
                        ? `Éligible : ${form.requirement}`
                        : `Inéligible : exige ${form.requirement}`
                  }
                >
                  <Chip
                    size="small"
                    label={form.label}
                    color={form.possible ? "success" : "default"}
                    variant={form.possible ? "filled" : "outlined"}
                    /* Une forme non servie est barrée : la base peut la
                       permettre, le moteur ne la rend pas. */
                    sx={form.served ? undefined : { textDecoration: "line-through", opacity: 0.6 }}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Paper>

          {staleOverrides.length > 0 ? (
            <Alert severity="warning" sx={{ mb: 2, alignItems: "flex-start" }}>
              {staleOverrides.length} correction(s) éditoriale(s) « à revoir » : elles visent un fait que la
              dernière extraction a renuméroté ou supprimé, donc elles ne s'appliquent plus à rien. Le fait
              qu'elles désignaient est peut-être aujourd'hui un autre : refaites-les à la main sur les lignes
              ci-dessous (pas de suppression possible depuis cet écran).
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                {staleOverrides.map((override) => (
                  <li key={override.fact_id}>
                    <span className="mono">{override.fact_id}</span> · {disabledByLabel(override.disabled_by)}
                    {override.note ? ` · « ${override.note} »` : ""}
                  </li>
                ))}
              </Box>
            </Alert>
          ) : null}
          {context?.stale ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Le chapitre a été réimporté sans ré-extraction : les passages affichés ne sont plus forcément
              ceux qui ont produit ces faits.
            </Alert>
          ) : null}

          <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            <Tabs
              value={tab}
              onChange={(_event, value: number) => setTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ px: 1, borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label={`Termes (${counts.terms})`} />
              <Tab label={`Relations (${counts.relations})`} />
              <Tab label={`Étapes (${counts.steps})`} />
              <Tab label={`Dates (${counts.dates})`} />
              <Tab label={`Idées fausses (${counts.misconceptions})`} />
              <Tab label={`Catégories (${counts.categories})`} />
            </Tabs>
            <Stack divider={<Divider flexItem />}>{factsList}</Stack>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ObjectiveKnowledgePanel;
